import type { ReactNode } from 'react'

import { faChevronDown } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { css, styled } from 'styled-components'

import { Colors } from '@b-stock/fe-core/design-system'

import { Card, CardTitle, type CardVariant, cardHeaderStyles } from './ui'

const Collapsed = styled.div``

const Details = styled.details`
  &[open] + ${Collapsed} {
    display: none;
  }
`

const Summary = styled.summary<{
  $hasCollapsedContent?: boolean
  $hideToggle?: boolean
  $variant?: CardVariant | undefined
}>`
  ${cardHeaderStyles}
  cursor: ${(o) => (o.$hideToggle ? 'default' : 'pointer')};
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  ${(o) =>
    !o.$hasCollapsedContent &&
    css`
      details:not([open]) > & {
        border-bottom: none;
      }
    `}
`

const ChevronIcon = styled(FontAwesomeIcon)`
  color: ${Colors.Semantic.Neutral.black};
  font-size: 1rem;

  details[open] & {
    transform: rotate(180deg);
  }
`

const HeaderRight = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;
`

export const AccordionCard = ({
  children,
  collapsedContent,
  defaultOpen = true,
  headerAction,
  hideToggle = false,
  title,
  variant,
}: {
  children: ReactNode
  /**
   * `children` is the expanded body (hidden by <details> when closed);
   * `collapsedContent` is its collapsed counterpart. Anything that should
   * persist across both states is composed into both by the caller.
   */
  collapsedContent?: ReactNode
  defaultOpen?: boolean
  headerAction?: ReactNode
  hideToggle?: boolean
  title: ReactNode
  variant?: CardVariant
}) => (
  <Card>
    <Details open={defaultOpen}>
      <Summary
        $hasCollapsedContent={!!collapsedContent}
        $hideToggle={hideToggle}
        $variant={variant}
        onClick={hideToggle ? (e) => e.preventDefault() : undefined}
      >
        <CardTitle $variant={variant}>{title}</CardTitle>
        <HeaderRight>
          {headerAction && (
            <span onClick={(e) => e.stopPropagation()}>{headerAction}</span>
          )}
          {!hideToggle && <ChevronIcon icon={faChevronDown} />}
        </HeaderRight>
      </Summary>
      {children}
    </Details>
    {collapsedContent && <Collapsed>{collapsedContent}</Collapsed>}
  </Card>
)
